import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LandingNavItemComponent } from '../landing-nav-item/landing-nav-item.component';
import { boostThemeEnum } from '@app/services/page.service';
import { LandingNavItem } from '../landing-nav-item/landing-nav-item.model';
import { ScoreLocalService } from '@app/pages/score/score.service';
import { PlanService } from '@app/pages/plan/plan.service';
import { HomeService } from '@app/pages/home/home.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-landing-plan',
  standalone: true,
  imports: [CommonModule, LandingNavItemComponent],
  templateUrl: './landing-plan.component.html',
  styleUrls: ['./landing-plan.component.scss']
})
export class LandingPlanComponent implements OnInit, OnDestroy {

  planNavItem: LandingNavItem = { 
    theme: boostThemeEnum.glacier,
    contentTheme: "starlight",
    title: "Make a Plan",
    content:
      "Plan your day's activities, making sure you play lots of star cards to help you reach the end of the day with a strong Safety Buffer.",
    action: "PLAN NOW",
    routerLink: "/plan-home",
  };

  private scoreCacheSubscription?: Subscription;
  private plansLoadedSubscription?: Subscription;
  private todayHandSubscription?: Subscription;

  userHasPlan: boolean = false;

  constructor(
    private scoreLocalService: ScoreLocalService,
    private planService: PlanService,
    private homeService: HomeService
  ) {}

  ngOnInit(): void {
    // Subscribe to score cache updates
    this.scoreCacheSubscription = this.scoreLocalService.scoreCacheUpdated.subscribe(() => {
      this.updatePlanNavItem();
    });

    // Subscribe to plans loaded updates
    this.plansLoadedSubscription = this.homeService.onPlansLoaded.subscribe(() => {
      this.updatePlanNavItem();
    });

    // A hand for today (FLIP-606) is read from the API, so redraw when that answer arrives or changes.
    this.todayHandSubscription = this.planService.todayHandChanged.subscribe(() => {
      this.updatePlanNavItem();
    });
    this.todayHandSubscription.add(this.planService.refreshTodayHand().subscribe());

    // Initial update
    this.updatePlanNavItem();
  }

  ngOnDestroy(): void {
    if (this.scoreCacheSubscription) {
      this.scoreCacheSubscription.unsubscribe();
    }
    if (this.plansLoadedSubscription) {
      this.plansLoadedSubscription.unsubscribe();
    }
    if (this.todayHandSubscription) {
      this.todayHandSubscription.unsubscribe();
    }
  }

  private updatePlanNavItem(): void {
    const scoresCountToday = this.scoreLocalService.getCachedScoresCountToday();
    const hasScoreToday = scoresCountToday !== null && scoresCountToday > 0;
    const planExistsToday = this.planService.hasHandOrPlanToday();

    // If a hand exists for today, show the open-hand option
    if (planExistsToday) {
      // Scenario 3: User has scored and made a hand
      // Scenario 4: User's score time has elapsed but user has a hand
      this.planNavItem = {
        theme: boostThemeEnum.glacier,
        title: "Today's Hand",
        content: "You can change today's hand at any time - it's ok to drop a card if the day is feeling too full, or add in extra star cards if you need a safety boost.",
        // FLIP-609: old plans are reached only from Plan Home's Recent hands.
        routerLink: "/plan-home",
        action: "OPEN HAND",
        contentTheme: "wild-hare",
      }
      this.userHasPlan = true;
    }
    // Scenario 2: User has scored and not made a hand
    else if (hasScoreToday && !planExistsToday) {
      this.planNavItem = {
        theme: boostThemeEnum.glacier,
        contentTheme: "starlight",
        title: "It's time to build your hand!",
        content: "Now that you've completed a scoring session and know what today's safety buffer looks like, you can build your hand for the day.",
        routerLink: "/plan-home",
        action: "BUILD HAND",
      }
    }
    // Scenario 1: User has not scored or made a hand
    else {
      this.planNavItem = {
        theme: boostThemeEnum.glacier,
        contentTheme: "starlight",
        title: "Build a Hand",
        content: "Build your day's hand, making sure you play lots of star cards to help you reach the end of the day with a strong Safety Buffer.",
        routerLink: "/plan-home",
        action: "BUILD HAND",
      }
    }
  }

}
